import * as Effect from "effect/Effect";
import { identity } from "effect/Function";
import * as Schema from "effect/Schema";
import * as SchemaTransformation from "effect/SchemaTransformation";
import * as Struct from "effect/Struct";
import { ProviderOptionSelections } from "./model.ts";
import {
  AuthSessionId,
  CommandId,
  EventId,
  IsoDateTime,
  MessageId,
  NonNegativeInt,
  ProjectId,
  ProviderItemId,
  ThreadId,
  TrimmedNonEmptyString,
  TurnId,
} from "./baseSchemas.ts";
import { ProviderInstanceId } from "./providerInstance.ts";
import { PublicActivity } from "./activityLabel.ts";

export const ORCHESTRATION_WS_METHODS = {
  dispatchCommand: "orchestration.dispatchCommand",
  replayEvents: "orchestration.replayEvents",
  getArchivedShellSnapshot: "orchestration.getArchivedShellSnapshot",
  subscribeShell: "orchestration.subscribeShell",
  subscribeThread: "orchestration.subscribeThread",
  fetchOlderThreadMessages: "orchestration.fetchOlderThreadMessages",
  fetchOlderThreadActivities: "orchestration.fetchOlderThreadActivities",
  getThreadActivityPayload: "orchestration.getThreadActivityPayload",
  getTurnInspection: "orchestration.getTurnInspection",
} as const;

/**
 * `ModelSelection` — selection of a model on a configured provider instance.
 *
 * The routing key is `instanceId` (a user-defined slug identifying one
 * configured provider instance). Drivers, credentials, working-directory
 * bindings, and any other per-instance state are recovered from the
 * runtime registry via the instance id.
 *
 * Wire legacy: persisted selections produced before the driver/instance
 * split carried a `provider: <driver-id>` field instead. The schema absorbs
 * that shape via a pre-decoding transform — `{provider, model}` is promoted
 * to `{instanceId: defaultInstanceIdForDriver(provider), model}`. No
 * post-decode compatibility code lives in the runtime; the transform is the
 * only compat surface.
 */
const ModelSelectionWire = Schema.Struct({
  instanceId: ProviderInstanceId,
  model: TrimmedNonEmptyString,
  options: Schema.optionalKey(ProviderOptionSelections),
});

// Source shape for persisted legacy payloads. Fields are typed as
// `Schema.Unknown` so malformed drafts still make it into the transform and
// fail validation through the target schema (with proper error messages)
// rather than at the source-struct layer where the error is less actionable.
const ModelSelectionSource = Schema.Struct({
  provider: Schema.optional(Schema.Unknown),
  instanceId: Schema.optional(Schema.Unknown),
  model: Schema.Unknown,
  options: Schema.optional(Schema.Unknown),
});

export const ModelSelection = ModelSelectionSource.pipe(
  Schema.decodeTo(
    ModelSelectionWire,
    SchemaTransformation.transformOrFail({
      decode: (raw) => {
        // Resolve the routing key: prefer an explicit `instanceId`; fall
        // back to promoting the legacy `provider` slug (the canonical
        // `defaultInstanceIdForDriver` mapping) so persisted rollout-era
        // payloads decode without data loss. The target schema brands the
        // string as `ProviderInstanceId`.
        const instanceIdSource =
          raw.instanceId !== undefined
            ? raw.instanceId
            : typeof raw.provider === "string"
              ? raw.provider
              : undefined;
        const base: Record<string, unknown> = {
          instanceId: instanceIdSource,
          model: raw.model,
        };
        if (raw.options !== undefined) base.options = raw.options;
        return Effect.succeed(base as typeof ModelSelectionWire.Encoded);
      },
      encode: (value) => {
        const base: Record<string, unknown> = {
          model: value.model,
          instanceId: value.instanceId,
        };
        if (value.options !== undefined) base.options = value.options;
        return Effect.succeed(base as typeof ModelSelectionSource.Encoded);
      },
    }),
  ),
);
export type ModelSelection = typeof ModelSelection.Type;

export const PROVIDER_SEND_TURN_MAX_INPUT_CHARS = 120_000;
export const PROVIDER_SEND_TURN_MAX_ATTACHMENTS = 50;
export const PROVIDER_SEND_TURN_MAX_IMAGE_BYTES = 20 * 1024 * 1024;
export const PROVIDER_SEND_TURN_MAX_FILE_BYTES = 1024 * 1024 * 1024;
// Ids are "<threadSegment>/<fileName>": thread segment up to 80 chars, file name
// up to 255, plus the separator and a possible "-N" collision suffix.
const CHAT_ATTACHMENT_ID_MAX_CHARS = 384;
// Correlation id is command id by design in this model.
export const CorrelationId = CommandId;
export type CorrelationId = typeof CorrelationId.Type;

// New attachment ids are thread-relative paths "<threadId>/<fileName>" addressing
// <workspaceRoot>/attachments/<threadId>/<fileName>. The trailing "/<fileName>" is
// optional so legacy flat ids ("<threadSegment>-<uuid>") persisted before the
// per-thread layout still decode on read (their files no longer resolve, but the
// thread stays loadable). Traversal is forbidden in either form; on-disk
// resolution applies defense-in-depth.
const CHAT_ATTACHMENT_ID_PATTERN = new RegExp(
  String.raw`^(?!\.\.?(?:/|$))[a-z0-9._-]+(?:/(?!\.\.?$)[^\x00-\x1f<>:"\\/|?*]+)?$`,
  "i",
);

const ChatAttachmentId = TrimmedNonEmptyString.check(
  Schema.isMaxLength(CHAT_ATTACHMENT_ID_MAX_CHARS),
  Schema.isPattern(CHAT_ATTACHMENT_ID_PATTERN),
);
export type ChatAttachmentId = typeof ChatAttachmentId.Type;

export const ChatImageAttachment = Schema.Struct({
  type: Schema.Literal("image"),
  id: ChatAttachmentId,
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100), Schema.isPattern(/^image\//i)),
  sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES)),
});
export type ChatImageAttachment = typeof ChatImageAttachment.Type;

export const ChatFileAttachment = Schema.Struct({
  type: Schema.Literal("file"),
  id: ChatAttachmentId,
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: Schema.String.check(Schema.isMaxLength(100)),
  sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_FILE_BYTES)),
});
export type ChatFileAttachment = typeof ChatFileAttachment.Type;

export const ChatAttachment = Schema.Union([ChatImageAttachment, ChatFileAttachment]);
export type ChatAttachment = typeof ChatAttachment.Type;

export const OrchestrationProject = Schema.Struct({
  id: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  defaultModelSelection: Schema.NullOr(ModelSelection),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  deletedAt: Schema.NullOr(IsoDateTime),
});
export type OrchestrationProject = typeof OrchestrationProject.Type;

export const OrchestrationMessageRole = Schema.Literals(["user", "assistant", "system"]);
export type OrchestrationMessageRole = typeof OrchestrationMessageRole.Type;

export const OrchestrationMessagePhase = Schema.Literals([
  "commentary",
  "final_answer",
  "question",
]);
export type OrchestrationMessagePhase = typeof OrchestrationMessagePhase.Type;

export const OrchestrationMessage = Schema.Struct({
  id: MessageId,
  role: OrchestrationMessageRole,
  text: Schema.String,
  attachments: Schema.optional(Schema.Array(ChatAttachment)),
  turnId: Schema.NullOr(TurnId),
  streaming: Schema.Boolean,
  phase: Schema.optionalKey(OrchestrationMessagePhase),
  /**
   * Monotonic orchestration-event sequence of the message's first
   * `thread.message-sent` event. Server-assigned; used as the canonical
   * ordering key so message order is immune to client clock skew.
   * Optional for backward compatibility with rows projected before
   * migration 031.
   */
  sequence: Schema.optional(NonNegativeInt),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type OrchestrationMessage = typeof OrchestrationMessage.Type;

export const OrchestrationSessionStatus = Schema.Literals([
  "idle",
  "starting",
  "running",
  "ready",
  "interrupted",
  "stopped",
  "error",
]);
export type OrchestrationSessionStatus = typeof OrchestrationSessionStatus.Type;

export const OrchestrationSession = Schema.Struct({
  threadId: ThreadId,
  status: OrchestrationSessionStatus,
  providerName: Schema.NullOr(TrimmedNonEmptyString),
  providerInstanceId: Schema.optional(ProviderInstanceId),
  activeTurnId: Schema.NullOr(TurnId),
  lastError: Schema.NullOr(TrimmedNonEmptyString),
  updatedAt: IsoDateTime,
});
export type OrchestrationSession = typeof OrchestrationSession.Type;

export const PUBLIC_SESSION_ERROR = "The agent could not complete the request.";

export const OrchestrationThreadActivityTone = Schema.Literals([
  "info",
  "tool",
  "approval",
  "error",
]);
export type OrchestrationThreadActivityTone = typeof OrchestrationThreadActivityTone.Type;

export const OrchestrationThreadActivity = Schema.Struct({
  id: EventId,
  tone: OrchestrationThreadActivityTone,
  kind: TrimmedNonEmptyString,
  summary: TrimmedNonEmptyString,
  /**
   * Payload of task and plain activities. Tool steps arrive without one: their
   * data waits for `getThreadActivityPayload`.
   */
  payload: Schema.optional(Schema.Unknown),
  /** Byte size of the payload JSON. */
  payloadBytes: Schema.optional(NonNegativeInt),
  /** Audience-safe label computed by the server's activity label policy. */
  public: Schema.optionalKey(PublicActivity),
  turnId: Schema.NullOr(TurnId),
  sequence: Schema.optional(NonNegativeInt),
  createdAt: IsoDateTime,
});
export type OrchestrationThreadActivity = typeof OrchestrationThreadActivity.Type;

const MODEL_IDENTITY_KEYS: ReadonlySet<string> = new Set([
  "model",
  "fromModel",
  "toModel",
  "modelUsage",
  "modelProvider",
  "reasoningEffort",
  "reasoning_effort",
  "modelReasoningEffort",
]);

function omitModelIdentity(value: unknown): unknown {
  if (Array.isArray(value)) {
    let next: Array<unknown> | null = null;
    value.forEach((entry, index) => {
      const cleaned = omitModelIdentity(entry);
      if (cleaned !== entry) {
        next ??= [...value];
        next[index] = cleaned;
      }
    });
    return next ?? value;
  }
  if (value === null || typeof value !== "object") return value;
  let next: Record<string, unknown> | null = null;
  for (const [key, entry] of Object.entries(value)) {
    if (MODEL_IDENTITY_KEYS.has(key)) {
      next ??= { ...value };
      delete next[key];
      continue;
    }
    const cleaned = omitModelIdentity(entry);
    if (cleaned !== entry) {
      next ??= { ...value };
      next[key] = cleaned;
    }
  }
  return next ?? value;
}

/** Activity payload as clients receive it: encoding drops every model identity key. */
const ClientActivityPayload = Schema.Unknown.pipe(
  Schema.decodeTo(
    Schema.Unknown,
    SchemaTransformation.transform({ decode: identity, encode: omitModelIdentity }),
  ),
);

export const ClientOrchestrationThreadActivity = OrchestrationThreadActivity.mapFields(
  Struct.assign({ payload: Schema.optional(ClientActivityPayload) }),
);
export type ClientOrchestrationThreadActivity = typeof ClientOrchestrationThreadActivity.Type;

export const ClientOrchestrationSession = OrchestrationSession.mapFields(
  Struct.omit(["providerName", "providerInstanceId"]),
);
export type ClientOrchestrationSession = typeof ClientOrchestrationSession.Type;

const OrchestrationLatestTurnState = Schema.Literals([
  "running",
  "interrupted",
  "completed",
  "error",
]);
export type OrchestrationLatestTurnState = typeof OrchestrationLatestTurnState.Type;

export const OrchestrationLatestTurn = Schema.Struct({
  turnId: TurnId,
  state: OrchestrationLatestTurnState,
  requestedAt: IsoDateTime,
  startedAt: Schema.NullOr(IsoDateTime),
  completedAt: Schema.NullOr(IsoDateTime),
  assistantMessageId: Schema.NullOr(MessageId),
});
export type OrchestrationLatestTurn = typeof OrchestrationLatestTurn.Type;

export const OrchestrationThreadBlock = Schema.Struct({
  topic: TrimmedNonEmptyString,
  title: TrimmedNonEmptyString,
  text: TrimmedNonEmptyString,
  blockedAt: IsoDateTime,
});
export type OrchestrationThreadBlock = typeof OrchestrationThreadBlock.Type;

export const ThreadKind = Schema.Literals(["root", "subagent"]);
export type ThreadKind = typeof ThreadKind.Type;
export const DEFAULT_THREAD_KIND: ThreadKind = "root";

export const OrchestrationThread = Schema.Struct({
  id: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  threadKind: ThreadKind.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_THREAD_KIND))),
  parentThreadId: Schema.NullOr(ThreadId).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  latestTurn: Schema.NullOr(OrchestrationLatestTurn),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  archivedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  block: Schema.NullOr(OrchestrationThreadBlock).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  deletedAt: Schema.NullOr(IsoDateTime),
  messages: Schema.Array(OrchestrationMessage),
  activities: Schema.Array(OrchestrationThreadActivity),
  session: Schema.NullOr(OrchestrationSession),
});
export type OrchestrationThread = typeof OrchestrationThread.Type;

export const OrchestrationReadModel = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  projects: Schema.Array(OrchestrationProject),
  threads: Schema.Array(OrchestrationThread),
  updatedAt: IsoDateTime,
});
export type OrchestrationReadModel = typeof OrchestrationReadModel.Type;

export const OrchestrationProjectShell = Schema.Struct({
  id: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  defaultModelSelection: Schema.NullOr(ModelSelection),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type OrchestrationProjectShell = typeof OrchestrationProjectShell.Type;

export const OrchestrationThreadShell = Schema.Struct({
  id: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  threadKind: ThreadKind.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_THREAD_KIND))),
  parentThreadId: Schema.NullOr(ThreadId).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  latestTurn: Schema.NullOr(OrchestrationLatestTurn),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  archivedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  block: Schema.NullOr(OrchestrationThreadBlock).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  session: Schema.NullOr(OrchestrationSession),
  latestUserMessageAt: Schema.NullOr(IsoDateTime),
});
export type OrchestrationThreadShell = typeof OrchestrationThreadShell.Type;

export const OrchestrationShellSnapshot = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  projects: Schema.Array(OrchestrationProjectShell),
  threads: Schema.Array(OrchestrationThreadShell),
  updatedAt: IsoDateTime,
});
export type OrchestrationShellSnapshot = typeof OrchestrationShellSnapshot.Type;

export const ClientOrchestrationProject = OrchestrationProject.mapFields(
  Struct.omit(["defaultModelSelection"]),
);
export type ClientOrchestrationProject = typeof ClientOrchestrationProject.Type;

export const ClientOrchestrationThread = OrchestrationThread.mapFields((fields) => ({
  ...Struct.omit(fields, ["modelSelection"]),
  activities: Schema.Array(ClientOrchestrationThreadActivity),
  session: Schema.NullOr(ClientOrchestrationSession),
}));
export type ClientOrchestrationThread = typeof ClientOrchestrationThread.Type;

export const ClientOrchestrationReadModel = OrchestrationReadModel.mapFields(
  Struct.assign({
    projects: Schema.Array(ClientOrchestrationProject),
    threads: Schema.Array(ClientOrchestrationThread),
  }),
);
export type ClientOrchestrationReadModel = typeof ClientOrchestrationReadModel.Type;

export const ClientOrchestrationProjectShell = OrchestrationProjectShell.mapFields(
  Struct.omit(["defaultModelSelection"]),
);
export type ClientOrchestrationProjectShell = typeof ClientOrchestrationProjectShell.Type;

export const ClientOrchestrationThreadShell = OrchestrationThreadShell.mapFields((fields) => ({
  ...Struct.omit(fields, ["modelSelection"]),
  session: Schema.NullOr(ClientOrchestrationSession),
}));
export type ClientOrchestrationThreadShell = typeof ClientOrchestrationThreadShell.Type;

export const ClientOrchestrationShellSnapshot = OrchestrationShellSnapshot.mapFields(
  Struct.assign({
    projects: Schema.Array(ClientOrchestrationProjectShell),
    threads: Schema.Array(ClientOrchestrationThreadShell),
  }),
);
export type ClientOrchestrationShellSnapshot = typeof ClientOrchestrationShellSnapshot.Type;

export const ClientOrchestrationShellStreamEvent = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("project-upserted"),
    sequence: NonNegativeInt,
    project: ClientOrchestrationProjectShell,
  }),
  Schema.Struct({
    kind: Schema.Literal("project-removed"),
    sequence: NonNegativeInt,
    projectId: ProjectId,
  }),
  Schema.Struct({
    kind: Schema.Literal("thread-upserted"),
    sequence: NonNegativeInt,
    thread: ClientOrchestrationThreadShell,
  }),
  Schema.Struct({
    kind: Schema.Literal("thread-removed"),
    sequence: NonNegativeInt,
    threadId: ThreadId,
  }),
]);
export type ClientOrchestrationShellStreamEvent = typeof ClientOrchestrationShellStreamEvent.Type;

export const ClientOrchestrationShellStreamItem = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("snapshot"),
    snapshot: ClientOrchestrationShellSnapshot,
  }),
  ClientOrchestrationShellStreamEvent,
  Schema.Struct({ kind: Schema.Literal("resync") }),
]);
export type ClientOrchestrationShellStreamItem = typeof ClientOrchestrationShellStreamItem.Type;

export const OrchestrationSubscribeThreadInput = Schema.Struct({
  threadId: ThreadId,
});
export type OrchestrationSubscribeThreadInput = typeof OrchestrationSubscribeThreadInput.Type;

export const MessageCursor = Schema.Struct({
  /**
   * Canonical `sequence` of the cursor message (server-assigned, monotonic,
   * clock-skew immune — see {@link OrchestrationMessage.sequence}). This is the
   * primary keyset ordering key; `createdAt`/`messageId` only break ties between
   * rows that share a `sequence` (i.e. legacy rows projected before the sequence
   * backfill, whose `sequence` is `null`). Keeping it in the cursor lets the
   * older-page query page in the same order as the full detail view instead of
   * by the (skewable) wall-clock `createdAt`.
   */
  sequence: Schema.NullOr(NonNegativeInt),
  createdAt: IsoDateTime,
  messageId: MessageId,
});
export type MessageCursor = typeof MessageCursor.Type;

export const OrchestrationThreadMessageWindow = Schema.Struct({
  hasMoreBefore: Schema.Boolean,
  oldestCursor: Schema.NullOr(MessageCursor),
});
export type OrchestrationThreadMessageWindow = typeof OrchestrationThreadMessageWindow.Type;

export const ActivityCursor = Schema.Struct({
  sequence: Schema.NullOr(NonNegativeInt),
  createdAt: IsoDateTime,
  activityId: EventId,
});
export type ActivityCursor = typeof ActivityCursor.Type;

export const OrchestrationThreadActivityWindow = Schema.Struct({
  hasMoreBefore: Schema.Boolean,
  oldestCursor: Schema.NullOr(ActivityCursor),
});
export type OrchestrationThreadActivityWindow = typeof OrchestrationThreadActivityWindow.Type;

/**
 * Text of an assistant message that is still being generated.
 *
 * Live text is transport-only: it lives in server memory, never in the event
 * log, and is superseded by the `thread.message-sent` event that closes the
 * message. `text` is exactly what an already-attached subscriber holds, so
 * {@link OrchestrationLiveMessageFrame} extends it without a gap.
 */
export const OrchestrationLiveMessage = Schema.Struct({
  messageId: MessageId,
  turnId: Schema.NullOr(TurnId),
  text: Schema.String,
  /** Server clock, so a provisional message sorts like the durable one will. */
  createdAt: IsoDateTime,
});
export type OrchestrationLiveMessage = typeof OrchestrationLiveMessage.Type;

/**
 * An increment of live assistant text.
 *
 * `emittedLength` is the total live length after applying `delta`, so a
 * subscriber can position the delta against the text it already holds and
 * append only the unseen tail. Frames are produced per subscription from the
 * server's authoritative text, so they never skip ahead of what that
 * subscriber received; a frame that still appears to (state lost across a
 * reconnect) is ignored, and the next snapshot or the closing message event
 * carries the authoritative full text.
 */
export const OrchestrationLiveMessageFrame = Schema.Struct({
  threadId: ThreadId,
  messageId: MessageId,
  turnId: Schema.NullOr(TurnId),
  delta: Schema.String,
  emittedLength: NonNegativeInt,
  /** Server clock, so a provisional message sorts like the durable one will. */
  createdAt: IsoDateTime,
});
export type OrchestrationLiveMessageFrame = typeof OrchestrationLiveMessageFrame.Type;

export const UserMessageRailEntry = Schema.Struct({
  messageId: MessageId,
  title: Schema.String,
  answer: Schema.NullOr(Schema.String),
});
export type UserMessageRailEntry = typeof UserMessageRailEntry.Type;

export const UserMessageRailUpdate = Schema.Struct({
  replace: Schema.Boolean,
  entries: Schema.Array(UserMessageRailEntry),
});
export type UserMessageRailUpdate = typeof UserMessageRailUpdate.Type;

export const ClientOrchestrationThreadDetailSnapshot = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  thread: ClientOrchestrationThread,
  messageWindow: OrchestrationThreadMessageWindow,
  activityWindow: OrchestrationThreadActivityWindow,
  /** In-flight assistant text held in server memory; see {@link OrchestrationLiveMessage}. */
  liveMessages: Schema.Array(OrchestrationLiveMessage),
});
export type ClientOrchestrationThreadDetailSnapshot =
  typeof ClientOrchestrationThreadDetailSnapshot.Type;

export const ProjectCreateCommand = Schema.Struct({
  type: Schema.Literal("project.create"),
  commandId: CommandId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  createWorkspaceRootIfMissing: Schema.optional(Schema.Boolean),
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
});

const ProjectMetaUpdateCommand = Schema.Struct({
  type: Schema.Literal("project.meta.update"),
  commandId: CommandId,
  projectId: ProjectId,
  title: Schema.optional(TrimmedNonEmptyString),
  workspaceRoot: Schema.optional(TrimmedNonEmptyString),
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
});

const ProjectDeleteCommand = Schema.Struct({
  type: Schema.Literal("project.delete"),
  commandId: CommandId,
  projectId: ProjectId,
  force: Schema.optional(Schema.Boolean),
});

const ThreadCreateCommand = Schema.Struct({
  type: Schema.Literal("thread.create"),
  commandId: CommandId,
  threadId: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  threadKind: Schema.optional(ThreadKind),
  parentThreadId: Schema.optional(Schema.NullOr(ThreadId)),
});

const ThreadDeleteCommand = Schema.Struct({
  type: Schema.Literal("thread.delete"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadArchiveCommand = Schema.Struct({
  type: Schema.Literal("thread.archive"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadUnarchiveCommand = Schema.Struct({
  type: Schema.Literal("thread.unarchive"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadMetaUpdateCommand = Schema.Struct({
  type: Schema.Literal("thread.meta.update"),
  commandId: CommandId,
  threadId: ThreadId,
  title: Schema.optional(TrimmedNonEmptyString),
  modelSelection: Schema.optional(ModelSelection),
});

const ThreadTurnStartBootstrapCreateThread = Schema.Struct({
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
});

const ThreadTurnStartBootstrap = Schema.Struct({
  createThread: Schema.optional(ThreadTurnStartBootstrapCreateThread),
});

export type ThreadTurnStartBootstrap = typeof ThreadTurnStartBootstrap.Type;

export const ThreadTurnStartCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.start"),
  commandId: CommandId,
  threadId: ThreadId,
  message: Schema.Struct({
    messageId: MessageId,
    role: Schema.Literal("user"),
    text: Schema.String,
    attachments: Schema.Array(ChatAttachment),
  }),
  modelSelection: Schema.optional(ModelSelection),
  titleSeed: Schema.optional(TrimmedNonEmptyString),
  bootstrap: Schema.optional(ThreadTurnStartBootstrap),
});

const ClientProjectCreateCommand = ProjectCreateCommand.mapFields(
  Struct.omit(["defaultModelSelection"]),
);

const ClientProjectMetaUpdateCommand = ProjectMetaUpdateCommand.mapFields(
  Struct.omit(["defaultModelSelection"]),
);

const ClientThreadCreateCommand = ThreadCreateCommand.mapFields(Struct.omit(["modelSelection"]));

const ClientThreadMetaUpdateCommand = ThreadMetaUpdateCommand.mapFields(
  Struct.omit(["modelSelection"]),
);

const ClientThreadTurnStartBootstrap = Schema.Struct({
  createThread: Schema.optional(
    ThreadTurnStartBootstrapCreateThread.mapFields(Struct.omit(["modelSelection"])),
  ),
});

const ClientThreadTurnStartCommand = ThreadTurnStartCommand.mapFields((fields) => ({
  ...Struct.omit(fields, ["modelSelection"]),
  deepWork: Schema.optional(Schema.Boolean),
  bootstrap: Schema.optional(ClientThreadTurnStartBootstrap),
}));

const ThreadTurnInterruptCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.interrupt"),
  commandId: CommandId,
  threadId: ThreadId,
  turnId: Schema.optional(TurnId),
});

const ThreadSessionStopCommand = Schema.Struct({
  type: Schema.Literal("thread.session.stop"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadBlockCommand = Schema.Struct({
  type: Schema.Literal("thread.block"),
  commandId: CommandId,
  threadId: ThreadId,
  topic: TrimmedNonEmptyString,
  title: TrimmedNonEmptyString,
  text: TrimmedNonEmptyString,
});

const ThreadUnblockCommand = Schema.Struct({
  type: Schema.Literal("thread.unblock"),
  commandId: CommandId,
  threadId: ThreadId,
});

const DispatchableClientOrchestrationCommand = Schema.Union([
  ProjectCreateCommand,
  ProjectMetaUpdateCommand,
  ProjectDeleteCommand,
  ThreadCreateCommand,
  ThreadDeleteCommand,
  ThreadArchiveCommand,
  ThreadUnarchiveCommand,
  ThreadMetaUpdateCommand,
  ThreadTurnStartCommand,
  ThreadTurnInterruptCommand,
  ThreadSessionStopCommand,
  ThreadBlockCommand,
  ThreadUnblockCommand,
]);
export type DispatchableClientOrchestrationCommand =
  typeof DispatchableClientOrchestrationCommand.Type;

export const ClientOrchestrationCommand = Schema.Union([
  ClientProjectCreateCommand,
  ClientProjectMetaUpdateCommand,
  ProjectDeleteCommand,
  ClientThreadCreateCommand,
  ThreadDeleteCommand,
  ThreadArchiveCommand,
  ThreadUnarchiveCommand,
  ClientThreadMetaUpdateCommand,
  ClientThreadTurnStartCommand,
  ThreadTurnInterruptCommand,
  ThreadSessionStopCommand,
  ThreadBlockCommand,
  ThreadUnblockCommand,
]);
export type ClientOrchestrationCommand = typeof ClientOrchestrationCommand.Type;

const ThreadSessionSetCommand = Schema.Struct({
  type: Schema.Literal("thread.session.set"),
  commandId: CommandId,
  threadId: ThreadId,
  session: OrchestrationSession,
  createdAt: IsoDateTime,
});

/**
 * Opens an assistant message in the event log before its text exists.
 *
 * The text itself streams through memory and reaches the log only when the
 * message completes, but the message still has to take its place in the
 * timeline the moment it starts: ordering runs on the event sequence, and a
 * message that claims one only at completion sorts after everything that
 * happened while it was being written.
 */
const ThreadMessageAssistantOpenCommand = Schema.Struct({
  type: Schema.Literal("thread.message.assistant.open"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  turnId: Schema.optional(TurnId),
});

const ThreadMessageAssistantDeltaCommand = Schema.Struct({
  type: Schema.Literal("thread.message.assistant.delta"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  delta: Schema.String,
  turnId: Schema.optional(TurnId),
});

const ThreadMessageAssistantCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.message.assistant.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  turnId: Schema.optional(TurnId),
  phase: Schema.optional(OrchestrationMessagePhase),
});

const ThreadTurnSettleCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.settle"),
  commandId: CommandId,
  threadId: ThreadId,
  turnId: TurnId,
  state: Schema.Literals(["completed", "interrupted", "error"]),
});

const ThreadActivityAppendCommand = Schema.Struct({
  type: Schema.Literal("thread.activity.append"),
  commandId: CommandId,
  threadId: ThreadId,
  activity: OrchestrationThreadActivity,
  createdAt: IsoDateTime,
});

const InternalOrchestrationCommand = Schema.Union([
  ThreadSessionSetCommand,
  ThreadTurnSettleCommand,
  ThreadMessageAssistantOpenCommand,
  ThreadMessageAssistantDeltaCommand,
  ThreadMessageAssistantCompleteCommand,
  ThreadActivityAppendCommand,
]);
export type InternalOrchestrationCommand = typeof InternalOrchestrationCommand.Type;

export const OrchestrationCommand = Schema.Union([
  DispatchableClientOrchestrationCommand,
  InternalOrchestrationCommand,
]);
export type OrchestrationCommand = typeof OrchestrationCommand.Type;

export const OrchestrationEventType = Schema.Literals([
  "project.created",
  "project.meta-updated",
  "project.deleted",
  "thread.created",
  "thread.deleted",
  "thread.archived",
  "thread.unarchived",
  "thread.blocked",
  "thread.unblocked",
  "thread.meta-updated",
  "thread.runtime-mode-set",
  "thread.interaction-mode-set",
  "thread.message-sent",
  "thread.turn-start-requested",
  "thread.turn-interrupt-requested",
  "thread.turn-settled",
  "thread.approval-response-requested",
  "thread.user-input-response-requested",
  "thread.session-stop-requested",
  "thread.session-set",
  "thread.proposed-plan-upserted",
  "thread.activity-appended",
]);
export type OrchestrationEventType = typeof OrchestrationEventType.Type;

export const OrchestrationAggregateKind = Schema.Literals(["project", "thread"]);
export type OrchestrationAggregateKind = typeof OrchestrationAggregateKind.Type;
export const OrchestrationActorKind = Schema.Literals(["client", "server", "provider"]);

export const OrchestrationCommandActor = Schema.Struct({
  subject: TrimmedNonEmptyString,
  sessionId: AuthSessionId,
});
export type OrchestrationCommandActor = typeof OrchestrationCommandActor.Type;

export const ProjectCreatedPayload = Schema.Struct({
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  defaultModelSelection: Schema.NullOr(ModelSelection),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ProjectMetaUpdatedPayload = Schema.Struct({
  projectId: ProjectId,
  title: Schema.optional(TrimmedNonEmptyString),
  workspaceRoot: Schema.optional(TrimmedNonEmptyString),
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
  updatedAt: IsoDateTime,
});

export const ProjectDeletedPayload = Schema.Struct({
  projectId: ProjectId,
  deletedAt: IsoDateTime,
});

export const ThreadCreatedPayload = Schema.Struct({
  threadId: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  threadKind: ThreadKind.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_THREAD_KIND))),
  parentThreadId: Schema.NullOr(ThreadId).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ThreadDeletedPayload = Schema.Struct({
  threadId: ThreadId,
  deletedAt: IsoDateTime,
});

export const ThreadArchivedPayload = Schema.Struct({
  threadId: ThreadId,
  archivedAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ThreadUnarchivedPayload = Schema.Struct({
  threadId: ThreadId,
  updatedAt: IsoDateTime,
});

export const ThreadBlockedPayload = Schema.Struct({
  threadId: ThreadId,
  block: OrchestrationThreadBlock,
});

export const ThreadUnblockedPayload = Schema.Struct({
  threadId: ThreadId,
  updatedAt: IsoDateTime,
});

export const ThreadMetaUpdatedPayload = Schema.Struct({
  threadId: ThreadId,
  title: Schema.optional(TrimmedNonEmptyString),
  modelSelection: Schema.optional(ModelSelection),
  updatedAt: IsoDateTime,
});

/**
 * Payload of event types kept in the stored event log for decoding only
 * (runtime/interaction mode changes, proposed plans, approval and user-input
 * responses). Stored fields beyond `threadId` are dropped on decode.
 */
export const LegacyThreadEventPayload = Schema.Struct({
  threadId: ThreadId,
});

export const ThreadMessageSentPayload = Schema.Struct({
  threadId: ThreadId,
  messageId: MessageId,
  role: OrchestrationMessageRole,
  text: Schema.String,
  attachments: Schema.optional(Schema.Array(ChatAttachment)),
  turnId: Schema.NullOr(TurnId),
  streaming: Schema.Boolean,
  phase: Schema.optionalKey(OrchestrationMessagePhase),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ThreadTurnStartRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  messageId: MessageId,
  modelSelection: Schema.optional(ModelSelection),
  titleSeed: Schema.optional(TrimmedNonEmptyString),
  createdAt: IsoDateTime,
});

export const ThreadTurnInterruptRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

export const ThreadTurnSettledPayload = Schema.Struct({
  threadId: ThreadId,
  turnId: TurnId,
  state: Schema.Literals(["completed", "interrupted", "error"]),
  createdAt: IsoDateTime,
});

export const ThreadSessionStopRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  createdAt: IsoDateTime,
});

export const ThreadSessionSetPayload = Schema.Struct({
  threadId: ThreadId,
  session: OrchestrationSession,
});

export const ThreadActivityAppendedPayload = Schema.Struct({
  threadId: ThreadId,
  activity: OrchestrationThreadActivity,
});

export const OrchestrationEventMetadata = Schema.Struct({
  providerTurnId: Schema.optional(TrimmedNonEmptyString),
  providerItemId: Schema.optional(ProviderItemId),
  adapterKey: Schema.optional(TrimmedNonEmptyString),
  ingestedAt: Schema.optional(IsoDateTime),
  actorSubject: Schema.optional(TrimmedNonEmptyString),
  actorSessionId: Schema.optional(AuthSessionId),
});
export type OrchestrationEventMetadata = typeof OrchestrationEventMetadata.Type;

const EventBaseFields = {
  sequence: NonNegativeInt,
  eventId: EventId,
  aggregateKind: OrchestrationAggregateKind,
  aggregateId: Schema.Union([ProjectId, ThreadId]),
  occurredAt: IsoDateTime,
  commandId: Schema.NullOr(CommandId),
  causationEventId: Schema.NullOr(EventId),
  correlationId: Schema.NullOr(CommandId),
  metadata: OrchestrationEventMetadata,
} as const;

const orchestrationEvent = <const Type extends OrchestrationEventType, Payload extends Schema.Top>(
  type: Type,
  payload: Payload,
) => Schema.Struct({ ...EventBaseFields, type: Schema.Literal(type), payload });

const ClientProjectCreatedPayload = ProjectCreatedPayload.mapFields(
  Struct.omit(["defaultModelSelection"]),
);

const ClientProjectMetaUpdatedPayload = ProjectMetaUpdatedPayload.mapFields(
  Struct.omit(["defaultModelSelection"]),
);

const ClientThreadCreatedPayload = ThreadCreatedPayload.mapFields(Struct.omit(["modelSelection"]));

const ClientThreadMetaUpdatedPayload = ThreadMetaUpdatedPayload.mapFields(
  Struct.omit(["modelSelection"]),
);

const ClientThreadTurnStartRequestedPayload = ThreadTurnStartRequestedPayload.mapFields(
  Struct.omit(["modelSelection"]),
);

const ClientThreadSessionSetPayload = ThreadSessionSetPayload.mapFields(
  Struct.assign({ session: ClientOrchestrationSession }),
);

const ClientThreadActivityAppendedPayload = ThreadActivityAppendedPayload.mapFields(
  Struct.assign({ activity: ClientOrchestrationThreadActivity }),
);

export const OrchestrationEvent = Schema.Union([
  orchestrationEvent("project.created", ProjectCreatedPayload),
  orchestrationEvent("project.meta-updated", ProjectMetaUpdatedPayload),
  orchestrationEvent("project.deleted", ProjectDeletedPayload),
  orchestrationEvent("thread.created", ThreadCreatedPayload),
  orchestrationEvent("thread.deleted", ThreadDeletedPayload),
  orchestrationEvent("thread.archived", ThreadArchivedPayload),
  orchestrationEvent("thread.unarchived", ThreadUnarchivedPayload),
  orchestrationEvent("thread.blocked", ThreadBlockedPayload),
  orchestrationEvent("thread.unblocked", ThreadUnblockedPayload),
  orchestrationEvent("thread.meta-updated", ThreadMetaUpdatedPayload),
  orchestrationEvent("thread.runtime-mode-set", LegacyThreadEventPayload),
  orchestrationEvent("thread.interaction-mode-set", LegacyThreadEventPayload),
  orchestrationEvent("thread.message-sent", ThreadMessageSentPayload),
  orchestrationEvent("thread.turn-start-requested", ThreadTurnStartRequestedPayload),
  orchestrationEvent("thread.turn-interrupt-requested", ThreadTurnInterruptRequestedPayload),
  orchestrationEvent("thread.turn-settled", ThreadTurnSettledPayload),
  orchestrationEvent("thread.approval-response-requested", LegacyThreadEventPayload),
  orchestrationEvent("thread.user-input-response-requested", LegacyThreadEventPayload),
  orchestrationEvent("thread.session-stop-requested", ThreadSessionStopRequestedPayload),
  orchestrationEvent("thread.session-set", ThreadSessionSetPayload),
  orchestrationEvent("thread.proposed-plan-upserted", LegacyThreadEventPayload),
  orchestrationEvent("thread.activity-appended", ThreadActivityAppendedPayload),
]);
export type OrchestrationEvent = typeof OrchestrationEvent.Type;

export const ClientOrchestrationEvent = Schema.Union([
  orchestrationEvent("project.created", ClientProjectCreatedPayload),
  orchestrationEvent("project.meta-updated", ClientProjectMetaUpdatedPayload),
  orchestrationEvent("project.deleted", ProjectDeletedPayload),
  orchestrationEvent("thread.created", ClientThreadCreatedPayload),
  orchestrationEvent("thread.deleted", ThreadDeletedPayload),
  orchestrationEvent("thread.archived", ThreadArchivedPayload),
  orchestrationEvent("thread.unarchived", ThreadUnarchivedPayload),
  orchestrationEvent("thread.blocked", ThreadBlockedPayload),
  orchestrationEvent("thread.unblocked", ThreadUnblockedPayload),
  orchestrationEvent("thread.meta-updated", ClientThreadMetaUpdatedPayload),
  orchestrationEvent("thread.runtime-mode-set", LegacyThreadEventPayload),
  orchestrationEvent("thread.interaction-mode-set", LegacyThreadEventPayload),
  orchestrationEvent("thread.message-sent", ThreadMessageSentPayload),
  orchestrationEvent("thread.turn-start-requested", ClientThreadTurnStartRequestedPayload),
  orchestrationEvent("thread.turn-interrupt-requested", ThreadTurnInterruptRequestedPayload),
  orchestrationEvent("thread.turn-settled", ThreadTurnSettledPayload),
  orchestrationEvent("thread.approval-response-requested", LegacyThreadEventPayload),
  orchestrationEvent("thread.user-input-response-requested", LegacyThreadEventPayload),
  orchestrationEvent("thread.session-stop-requested", ThreadSessionStopRequestedPayload),
  orchestrationEvent("thread.session-set", ClientThreadSessionSetPayload),
  orchestrationEvent("thread.proposed-plan-upserted", LegacyThreadEventPayload),
  orchestrationEvent("thread.activity-appended", ClientThreadActivityAppendedPayload),
]);
export type ClientOrchestrationEvent = typeof ClientOrchestrationEvent.Type;

export const ClientOrchestrationThreadStreamItem = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("rail"),
    update: UserMessageRailUpdate,
  }),
  Schema.Struct({
    kind: Schema.Literal("snapshot"),
    snapshot: ClientOrchestrationThreadDetailSnapshot,
  }),
  Schema.Struct({
    kind: Schema.Literal("event"),
    event: ClientOrchestrationEvent,
  }),
  Schema.Struct({
    kind: Schema.Literal("live"),
    live: OrchestrationLiveMessageFrame,
  }),
]);
export type ClientOrchestrationThreadStreamItem = typeof ClientOrchestrationThreadStreamItem.Type;

export const OrchestrationCommandReceiptStatus = Schema.Literals(["accepted", "rejected"]);
export type OrchestrationCommandReceiptStatus = typeof OrchestrationCommandReceiptStatus.Type;

export const ProviderSessionRuntimeStatus = Schema.Literals([
  "starting",
  "running",
  "stopped",
  "error",
]);
export type ProviderSessionRuntimeStatus = typeof ProviderSessionRuntimeStatus.Type;

const ProjectionThreadTurnStatus = Schema.Literals([
  "running",
  "completed",
  "interrupted",
  "error",
]);
export type ProjectionThreadTurnStatus = typeof ProjectionThreadTurnStatus.Type;

export const DispatchResult = Schema.Struct({
  sequence: NonNegativeInt,
});
export type DispatchResult = typeof DispatchResult.Type;

export const OrchestrationReplayEventsInput = Schema.Struct({
  fromSequenceExclusive: NonNegativeInt,
});
export type OrchestrationReplayEventsInput = typeof OrchestrationReplayEventsInput.Type;

const OrchestrationReplayEventsResult = Schema.Array(ClientOrchestrationEvent);
export type OrchestrationReplayEventsResult = typeof OrchestrationReplayEventsResult.Type;

export const OrchestrationFetchOlderThreadMessagesInput = Schema.Struct({
  threadId: ThreadId,
  beforeCursor: MessageCursor,
  limit: NonNegativeInt,
});
export type OrchestrationFetchOlderThreadMessagesInput =
  typeof OrchestrationFetchOlderThreadMessagesInput.Type;

export const OrchestrationFetchOlderThreadMessagesResult = Schema.Struct({
  messages: Schema.Array(OrchestrationMessage),
  hasMoreBefore: Schema.Boolean,
  oldestCursor: Schema.NullOr(MessageCursor),
});
export type OrchestrationFetchOlderThreadMessagesResult =
  typeof OrchestrationFetchOlderThreadMessagesResult.Type;

export const OrchestrationFetchOlderThreadActivitiesInput = Schema.Struct({
  threadId: ThreadId,
  beforeCursor: ActivityCursor,
  limit: NonNegativeInt,
});
export type OrchestrationFetchOlderThreadActivitiesInput =
  typeof OrchestrationFetchOlderThreadActivitiesInput.Type;

export const OrchestrationFetchOlderThreadActivitiesResult = Schema.Struct({
  activities: Schema.Array(ClientOrchestrationThreadActivity),
  hasMoreBefore: Schema.Boolean,
  oldestCursor: Schema.NullOr(ActivityCursor),
});
export type OrchestrationFetchOlderThreadActivitiesResult =
  typeof OrchestrationFetchOlderThreadActivitiesResult.Type;

export const OrchestrationGetThreadActivityPayloadInput = Schema.Struct({
  threadId: ThreadId,
  activityId: EventId,
});
export type OrchestrationGetThreadActivityPayloadInput =
  typeof OrchestrationGetThreadActivityPayloadInput.Type;

export const OrchestrationGetThreadActivityPayloadResult = Schema.Struct({
  activityId: EventId,
  payload: ClientActivityPayload,
});
export type OrchestrationGetThreadActivityPayloadResult =
  typeof OrchestrationGetThreadActivityPayloadResult.Type;

export const OrchestrationGetTurnInspectionInput = Schema.Struct({
  threadId: ThreadId,
  turnId: TurnId,
});
export type OrchestrationGetTurnInspectionInput = typeof OrchestrationGetTurnInspectionInput.Type;

export const OrchestrationTurnTokenUsage = Schema.Struct({
  inputTokens: NonNegativeInt,
  /** Cache-read slice of `inputTokens`. */
  cachedInputTokens: NonNegativeInt,
  outputTokens: NonNegativeInt,
});
export type OrchestrationTurnTokenUsage = typeof OrchestrationTurnTokenUsage.Type;

/** Carries model identity: served to `access:write` sessions only. */
export const OrchestrationTurnInspection = Schema.Struct({
  turnId: TurnId,
  modelSelection: Schema.NullOr(ModelSelection),
  tokenUsage: Schema.NullOr(OrchestrationTurnTokenUsage),
  startedAt: Schema.NullOr(IsoDateTime),
  completedAt: Schema.NullOr(IsoDateTime),
});
export type OrchestrationTurnInspection = typeof OrchestrationTurnInspection.Type;

export const OrchestrationRpcSchemas = {
  dispatchCommand: {
    input: ClientOrchestrationCommand,
    output: DispatchResult,
  },
  replayEvents: {
    input: OrchestrationReplayEventsInput,
    output: OrchestrationReplayEventsResult,
  },
  getArchivedShellSnapshot: {
    input: Schema.Struct({}),
    output: ClientOrchestrationShellSnapshot,
  },
  subscribeThread: {
    input: OrchestrationSubscribeThreadInput,
    output: ClientOrchestrationThreadStreamItem,
  },
  subscribeShell: {
    input: Schema.Struct({ afterSequence: Schema.optional(NonNegativeInt) }),
    output: ClientOrchestrationShellStreamItem,
  },
  fetchOlderThreadMessages: {
    input: OrchestrationFetchOlderThreadMessagesInput,
    output: OrchestrationFetchOlderThreadMessagesResult,
  },
  fetchOlderThreadActivities: {
    input: OrchestrationFetchOlderThreadActivitiesInput,
    output: OrchestrationFetchOlderThreadActivitiesResult,
  },
  getThreadActivityPayload: {
    input: OrchestrationGetThreadActivityPayloadInput,
    output: OrchestrationGetThreadActivityPayloadResult,
  },
  getTurnInspection: {
    input: OrchestrationGetTurnInspectionInput,
    output: OrchestrationTurnInspection,
  },
} as const;

export class OrchestrationGetSnapshotError extends Schema.TaggedErrorClass<OrchestrationGetSnapshotError>()(
  "OrchestrationGetSnapshotError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export class OrchestrationDispatchCommandError extends Schema.TaggedErrorClass<OrchestrationDispatchCommandError>()(
  "OrchestrationDispatchCommandError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export class OrchestrationReplayEventsError extends Schema.TaggedErrorClass<OrchestrationReplayEventsError>()(
  "OrchestrationReplayEventsError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}
